import {
  Amount,
  PaymentFrequency,
} from "@evergiving/common/dist/es2015/pledge";

import { StripeExpressCheckoutElementOptions } from "@stripe/stripe-js";

// stripe-js declares these in elements/apple-pay but its index does not
// re-export them, so name them through the element options instead of
// reaching into the package's internal path.
type ApplePayRecurringPaymentRequest = NonNullable<
  NonNullable<
    StripeExpressCheckoutElementOptions["applePay"]
  >["recurringPaymentRequest"]
>;

type ApplePayRecurringPaymentRequestIntervalUnit = NonNullable<
  ApplePayRecurringPaymentRequest["regularBilling"]["recurringPaymentIntervalUnit"]
>;

/**
 * Apple shows this link on the payment sheet and keeps it in the donor's
 * Wallet, so it must be a page Apple can reach over https. Only production and
 * staging have one; dev and the VDE point at staging, because nobody follows
 * the link there and a local host would fail.
 */
const managementUrl =
  process.env.EVERGIVING_ENVIRONMENT === "production"
    ? "https://manage.evergiving.com"
    : "https://app.waysactstaging.com";

type Interval = {
  count: number;
  unit: ApplePayRecurringPaymentRequestIntervalUnit;
};

/**
 * How each frequency the form offers looks to Apple. Apple has no week unit,
 * so the sub-monthly cycles count in days. One-time is absent: it does not
 * repeat, so it gets no recurring request at all.
 */
const intervals: {
  [key in Exclude<PaymentFrequency, PaymentFrequency.OneTime>]: Interval;
} = {
  [PaymentFrequency.Weekly]: { count: 7, unit: "day" },
  [PaymentFrequency.Fortnightly]: { count: 14, unit: "day" },
  [PaymentFrequency.FourWeekly]: { count: 28, unit: "day" },
  [PaymentFrequency.Monthly]: { count: 1, unit: "month" },
  [PaymentFrequency.Quarterly]: { count: 3, unit: "month" },
  [PaymentFrequency.HalfYearly]: { count: 6, unit: "month" },
  [PaymentFrequency.Annual]: { count: 1, unit: "year" },
};

/**
 * What to tell Apple Pay about a donation that repeats. Apple answers a
 * recurring request with a merchant token (MPAN) where the issuer supports
 * one, and a device token (DPAN) otherwise. An MPAN survives the donor
 * changing device, so a recurring donation keeps working.
 *
 * Undefined for a gift that does not repeat: Apple must not be told a one-off
 * payment recurs.
 */
export const recurringPaymentRequestFor = ({
  frequency,
  amount,
  charityName,
}: {
  frequency: PaymentFrequency | undefined;
  amount: Amount;
  charityName: string;
}): ApplePayRecurringPaymentRequest | undefined => {
  if (frequency === undefined || frequency === PaymentFrequency.OneTime) {
    return undefined;
  }

  const { count, unit } = intervals[frequency];

  return {
    paymentDescription: charityName,
    managementURL: managementUrl,
    regularBilling: {
      label: charityName,
      amount: amount.value,
      recurringPaymentIntervalUnit: unit,
      recurringPaymentIntervalCount: count,
    },
  };
};
